import React from "react";
import Table from "../base-table";
import type { TableRef } from "../base-table";
import type {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  ColumnOrderState,
  RowSelectionState,
  PaginationState,
  ExpandedState,
  TableMeta,
} from "@tanstack/react-table";
import { getCoreRowModel } from "@tanstack/react-table";
import { Shipment } from "../../graphql/graphql";
import { TableMessage } from "../../shared/TableMessage";
import { TableSpinner } from "../../shared/TableSpinner";

interface ShipmentsTableProps {
  tableRef?: React.Ref<TableRef>;
  columns?: () => ColumnDef<Shipment, any>[];
  data: Shipment[];
  getRowId?: (originalRow: Shipment) => string;
  enableRowSelection?: boolean;
  enableColumnFiltersSidePanel?: boolean;
  enablePagination?: boolean;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageIndex?: number;
  pageSize?: number;
  totalCount?: number;
  state: {
    sorting: SortingState;
    columnFilters: ColumnFiltersState;
    setSorting: (newSorting: SortingState) => void;
    setColumnFilters: (newFilters: ColumnFiltersState) => void;
    columnOrder: ColumnOrderState;
    setColumnOrder: (newOrder: ColumnOrderState) => void;
    setRowSelection: (newRowSelection: RowSelectionState) => void;
    rowSelection?: RowSelectionState;
    resetRowSelection?: (defaultState?: boolean) => void;
    columnVisibility?: Record<string, boolean>;
    setColumnVisibility?: (newVisibility: Record<string, boolean>) => void;
    pagination?: { totalCount?: number };
    globalSearch?: string;
    expanded?: ExpandedState;
    columnFiltersSidePanelIsOpen?: boolean;
  };
  isLoading: boolean;
  error: any;
  enableGlobalFilter?: boolean;
  onGlobalFilterChange?: (filter: string) => void;
  onExpandedChange?: (expanded: ExpandedState) => void;
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
  meta: TableMeta<Shipment>;
  renderExpandedContent?: (shipment: Shipment) => React.ReactNode;
}

export const ShipmentsTable = React.memo(function ShipmentsTable({
  tableRef,
  columns,
  data,
  state,
  isLoading,
  error,
  getRowId,
  enablePagination,
  enableRowSelection,
  pageIndex = 0,
  pageSize = 10,
  totalCount,
  onPaginationChange,
  enableGlobalFilter,
  onGlobalFilterChange,
  onExpandedChange,
  meta,
  onColumnFiltersSidePanelOpenChange,
  renderExpandedContent,
}: ShipmentsTableProps & {
  onColumnFiltersSidePanelOpenChange?: (isOpen: boolean) => void;
}) {

  if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

  if (data) {
    return (
      <div style={{ height: "100%" }}>
        <Table
          className="shipments-table"
          fullWidth
          ref={tableRef}
          columns={columns ? (columns() as any) : []}
          getCoreRowModel={getCoreRowModel()}
          data={data}
          enableColumnFilters={false}
          enableColumnFiltersSidePanel={false}
          enableSorting={true}
          disableStickyHeader={true}
          allowMultiSort={false}
          enableRowSelection={enableRowSelection}
          enablePagination={enablePagination}
          manualPagination={true}
          manualFiltering={true}
          manualSorting={true}
          enableMultiRowSelection={false}
          enableGlobalFilter={enableGlobalFilter}
          initialGlobalFilter={state.globalSearch}
          onPaginationChange={onPaginationChange}
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalCount={state.pagination?.totalCount}
          state={{
            columnFilters: state.columnFilters,
            sorting: state.sorting,
            columnOrder: state.columnOrder,
            setColumnOrder: state.setColumnOrder,
            rowSelection: state.rowSelection,
            columnVisibility: state.columnVisibility,
            expanded: state.expanded,
            columnFiltersSidePanelIsOpen: state.columnFiltersSidePanelIsOpen,
          }}
          onSortChange={state.setSorting}
          onColumnFiltersChange={state.setColumnFilters}
          onRowSelectionChange={state.setRowSelection}
          onGlobalFilterChange={onGlobalFilterChange}
          getRowId={getRowId}
          resetRowSelection={state.resetRowSelection}
          renderExpandedContent={renderExpandedContent}
          onExpandedChange={onExpandedChange}
          onColumnFiltersSidePanelOpenChange={onColumnFiltersSidePanelOpenChange}
          meta={meta}
          noDataText={isLoading ? "Loading shipments..." : "No shipments found."}
          noDataContent={isLoading ? <TableSpinner /> : <TableMessage msg="No Shipments Found" />}
        />
      </div>
    );
  }
  return <></>;
});